import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import Image from 'next/image';
import config from '../lib/config';
import PostBody from '../components/post-body';
import ScalingIframe from '../components/scaling-iframe';

import { GetStaticPaths, GetStaticProps } from 'next';
import { getPageSlugs, getPrimaryMenuItems, getSinglePage } from '../lib/api';
import { ISponsor } from '../models/sponsor';
import { IMenuItem } from '../models/menu-item';
import { IPostDirect } from '../models/post';
import { H1, H2, P } from '../components/text';
import { CgSpinnerAlt } from 'react-icons/cg';

const excludedPaths = new Set([
	'klassement',
	'periodeklassement',
	'programma',
	'resultaten',
	'spelerstatistieken',
	'statistieken',
	'teams',
	'transfers',
	'video',
	'video-overig',
	'samenvattingen',
	'zoeken',
	'mvo',
	'cybercontact',
	'uitslagen',
	'speler-van-de-week',
	'video-archief',
	'bal',
	'doelpunt-van-de-week',
	'stemmen',
	'partners',
	'first',
]);

interface IProps {
	data: IPostDirect;
	sponsors: ISponsor[];
	primaryMenuItems: IMenuItem[];
}

const iframeComponents: { [key: string]: React.JSX.Element } = {
	'stemmen-schilden': (
		<div className="py-12">
			<ScalingIframe url="https://api.keukenkampioendivisie.nl/formulier-stemmen-schilden/" />
		</div>
	),
	'gouden-schilden': (
		<div className="py-12">
			<ScalingIframe url="https://api.keukenkampioendivisie.nl/formulier-gouden-schilden/" />
		</div>
	),
	'slam-3': (
		<div className="py-12">
			<ScalingIframe url="https://api.keukenkampioendivisie.nl/formulier-gouden-schilden/" />
		</div>
	),
	nieuwsbrief: (
		<div className="py-12">
			<ScalingIframe url="https://api.keukenkampioendivisie.nl/nieuwsbrief-2/" />
		</div>
	),
	contact: (
		<div className="py-12">
			<ScalingIframe url="https://api.keukenkampioendivisie.nl/contactformulier/" />
		</div>
	),
};

export default function Page({ data, sponsors, primaryMenuItems }: IProps) {
	const pageTitle = `${data.title} | ${config.siteName}`;
	const metaDesc = data.seo?.description || data.title;

	const [loading, setLoading] = useState(true);

	useEffect(() => {
		setLoading(false);
	}, []);

	const svgSpin = <CgSpinnerAlt className="animate-spin" />;

	if (loading) {
		return (
			<div className="flex flex-col items-center py-6">
				{svgSpin}
				<p></p>
			</div>
		);
	}

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={metaDesc}>
			<Head>
				<title>{`${pageTitle}`}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			{data.featuredImage && (
				<div className="relative h-[540px] w-full">
					<Image
						width={1536}
						height={832}
						src={data.featuredImage.node.sourceUrl}
						alt={data.featuredImage.node.altText}
						className="object-cover object-top aspect-video-16/9 w-full h-full"
					/>
					<div className="kkd-gradient-red-overlay z-0"></div>
					<div className="kkd-gradient-dark-overlay z-0"></div>

					<Container className="py-12 lg:py-0">
						<div className="absolute top-1/2 transform -translate-y-1/2 flex flex-col z-20">
							<div className="flex items-center">
								<div className="flex-grow">
									<H1 className="text-white mb-2 drop-shadow-md ">{data.title}</H1>
									{data.excerpt && (
										<P
											className="max-w-7xl text-white pr-12"
											dangerouslySetInnerHTML={{ __html: data.excerpt }}
										/>
									)}
								</div>
							</div>
						</div>
					</Container>
				</div>
			)}

			<Container className="py-12 xl:py-16 max-w-5xl">
				<article>
					{!data.featuredImage && <H2 className="text-center py-6">{data.title}</H2>}
					{data.content && <PostBody content={data.content} />}
					{iframeComponents[data.slug]}
				</article>
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
	const { pages, sponsors } = await getSinglePage(params?.pageSlug as string);

	if (!pages.nodes?.length) {
		return {
			redirect: {
				destination: `/nieuws/${params?.pageSlug}`,
				statusCode: 302,
			},
		};
	}

	const primaryMenuItems = await getPrimaryMenuItems();

	return {
		revalidate: 3600,
		props: {
			data: pages.nodes[0],
			sponsors: sponsors.nodes,
			primaryMenuItems,
		},
	};
};

export const getStaticPaths: GetStaticPaths = async () => {
	const pageSlugs = await getPageSlugs();

	const filteredPaths = pageSlugs
		.map((node) => ({ params: { pageSlug: node.slug } }))
		.filter((path) => !excludedPaths.has(path.params.pageSlug));

	return {
		paths: filteredPaths,
		fallback: 'blocking',
	};
};
